import React, { useState, useEffect } from "react";
import { IonContent, IonHeader, IonPage, IonLoading, IonModal, useIonViewWillEnter } from "@ionic/react";
import LoginModal from "../../components/LoginModal/LoginModal";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import "./Tab3.css";
import { useUserData } from "../../context/UserDataFetcher";
import { useHistory } from "react-router-dom";
import ExcelImportButton from "../../components/ExcelImportButton/ExcelImportButton";
import { getPortalUrl } from "../../services/stripeService";
import { FirebaseApp } from "firebase/app";
import AddUserModel from "../../components/AddEmailModal/AddAuthorizedEmail"; // Make sure this path is correct
import { useScanner } from "../../context/ScannerContext";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreViewAppBar from "../../components/AppBar/MoreViewAppBar";
import { useDeveloperMode } from "../../context/DeveloperModeContext";

const Tab3: React.FC<{ app: FirebaseApp }> = ({ app }) => {
  const { stopScanner, isScannerActive } = useScanner();
  const { developerMode } = useDeveloperMode();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const userData = useUserData();
  const dealerId = userData?.dealerId || "defaultDealerId";
  const history = useHistory();
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  useIonViewWillEnter(() => {
    if (isScannerActive) {
      stopScanner();
    }
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(app), (currentUser) => {
      setIsUserLoggedIn(!!currentUser);
    });
    return () => unsubscribe();
  }, [app]);

  const handleSignOut = async () => {
    try {
      await signOut(getAuth(app));
      setIsUserLoggedIn(false);
      history.push("/");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleManageSubscriptions = async () => {
    setShowLoading(true);
    getPortalUrl(app)
      .then((url) => {
        window.location.href = url;
      })
      .catch((error) => {
        console.error("Failed to get the portal URL:", error);
      })
      .finally(() => {
        setShowLoading(false);
      });
  };

  const goToSettings = () => {
    history.push("/settings");
  };

  // Check if the user is an "Account Owner"
  const isAccountOwner = userData?.userType === "Account Owner";
  const isDriver = userData?.role === "driver";

  return (
    <IonPage>
      <IonHeader>
        <MoreViewAppBar position="static" />
      </IonHeader>
      <IonContent>
        {!isUserLoggedIn && (
          <LoginModal
            onLoginSuccess={() => setIsUserLoggedIn(true)}
            showModal={false}
            setShowModal={function (show: boolean): void {
              throw new Error("Function not implemented.");
            }}
          />
        )}
        {isUserLoggedIn && (
          <>
            <div className="navigate-button-container">
              <List component="nav" aria-label="account actions">
                <ListItemButton onClick={() => history.push("/shipments")}> 
                  <ListItemIcon>
                    <LocalShippingIcon />
                  </ListItemIcon>
                  <ListItemText primary="Shipments" />
                </ListItemButton>
                {!isDriver && (
                  <>
                    <Divider />
                    <ListItemButton onClick={() => history.push("/orders")}>
                      <ListItemIcon>
                        <AssignmentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Orders" />
                    </ListItemButton>
                  </>
                )}
                <Divider />
                <ListItemButton onClick={handleSignOut}>
                  <ListItemIcon>
                    <ExitToAppIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign Out" />
                </ListItemButton>
                <Divider />
                <ListItemButton onClick={goToSettings}>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                </ListItemButton>
                <Divider />
                {developerMode && (
                  <>
                    <ListItemButton onClick={handleManageSubscriptions}>
                      <ListItemIcon>
                        <PaymentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Manage Subscriptions" />
                    </ListItemButton>
                    <Divider />
                  </>
                )}
                {isAccountOwner && (
                  <>
                    <ListItemButton onClick={() => setShowAddUserModal(true)}>
                      <ListItemIcon>
                        <PersonAddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Add User" />
                    </ListItemButton>
                    <Divider />
                  </>
                )}
                {developerMode && (
                  <ListItemButton
                    onClick={() => {
                      /* keep ExcelImportButton behavior by delegating click */
                    }}
                  >
                    <ListItemIcon>
                      <UploadFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={<ExcelImportButton dealerId={dealerId} />}
                    />
                  </ListItemButton>
                )}
              </List>
            </div>
          </>
        )}
        <IonLoading isOpen={showLoading} message={"Please wait..."} />
        <IonModal
          isOpen={showAddUserModal}
          onDidDismiss={() => setShowAddUserModal(false)}
        >
          <AddUserModel
            isOpen={showAddUserModal}
            onClose={() => setShowAddUserModal(false)}
          />
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Tab3;
